import { useEffect, useMemo, useState } from "react";
import {
  User,
  Package,
  MapPin,
  FileText,
  Loader2,
  type LucideIcon,
} from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@project/components/ui/dialog";
import { Badge } from "@project/components/ui/badge";
import { Label } from "@project/components/ui/label";
import { formatDate } from "@project/lib/date-format";
import { getApiErrorMessage } from "@project/lib/api-error";
import { adminItemDonationService } from "@project/services/admin/item-donation.service";
import { adminScrapDonationService } from "@project/services/admin/scrap-donation.service";
import {
  donationRequestStatusClass,
  formatDonationQuantityLabel,
  formatDonationRequestStatusLabel,
  type ItemDonationRecord,
  type ScrapDonationRecord,
} from "@project/types/item-donation";

type DonationRequestVariant = "in-kind" | "scrap";

type SectionConfig = {
  label: string;
  icon: LucideIcon;
  subtitle: string;
};

const IN_KIND_SECTIONS: SectionConfig[] = [
  {
    label: "Donor Information",
    icon: User,
    subtitle: "Contact details of the person submitting the request",
  },
  {
    label: "Item Details",
    icon: Package,
    subtitle: "Category, item name, quantity, and preferred date",
  },
  {
    label: "Pickup Details",
    icon: MapPin,
    subtitle: "Address and location for item collection",
  },
  {
    label: "Notes & Review",
    icon: FileText,
    subtitle: "Message, admin remarks, and request status",
  },
];

const SCRAP_SECTIONS: SectionConfig[] = [
  {
    label: "Donor Information",
    icon: User,
    subtitle: "Contact details of the person submitting the request",
  },
  {
    label: "Item Details",
    icon: Package,
    subtitle: "Scrap category, item name, and quantity",
  },
  {
    label: "Pickup Details",
    icon: MapPin,
    subtitle: "Address and location for scrap collection",
  },
  {
    label: "Notes & Review",
    icon: FileText,
    subtitle: "Message, photo, remarks, and request status",
  },
];

function statusBadge(status?: string | null) {
  const label = formatDonationRequestStatusLabel(status) ?? "—";
  return <Badge className={donationRequestStatusClass(status)}>{label}</Badge>;
}

function displayValue(value?: string | number | null) {
  if (value === null || value === undefined || value === "") {
    return "—";
  }
  return String(value);
}

function ViewField({
  label,
  value,
  full = false,
}: {
  label: string;
  value?: string | number | null;
  full?: boolean;
}) {
  return (
    <div className={full ? "md:col-span-2" : ""}>
      <Label className="text-foreground/80 mb-1.5 block text-xs font-medium">
        {label}
      </Label>
      <div className="bg-muted/40 whitespace-pre-wrap rounded-xl border border-transparent px-3 py-2.5 text-sm text-foreground">
        {displayValue(value)}
      </div>
    </div>
  );
}

function SectionPanel({
  section,
  children,
}: {
  section: SectionConfig;
  children: React.ReactNode;
}) {
  const Icon = section.icon;
  return (
    <div className="rounded-2xl border border-border bg-card p-5 shadow-sm sm:p-6">
      <div className="mb-5 flex items-start gap-3 border-b border-border pb-4">
        <div className="bg-primary/10 grid h-11 w-11 shrink-0 place-items-center rounded-xl text-primary">
          <Icon className="h-5 w-5" />
        </div>
        <div>
          <h3 className="font-serif text-lg font-bold text-foreground sm:text-xl">
            {section.label}
          </h3>
          <p className="mt-0.5 text-xs text-muted-foreground sm:text-sm">
            {section.subtitle}
          </p>
        </div>
      </div>
      <div className="grid gap-4 md:grid-cols-2">{children}</div>
    </div>
  );
}

export type DonationRequestDetailDialogProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  requestId: number | null;
  variant: DonationRequestVariant;
  preview?: ItemDonationRecord | ScrapDonationRecord | null;
};

export function DonationRequestDetailDialog({
  open,
  onOpenChange,
  requestId,
  variant,
  preview,
}: DonationRequestDetailDialogProps) {
  const sections = useMemo(
    () => (variant === "scrap" ? SCRAP_SECTIONS : IN_KIND_SECTIONS),
    [variant]
  );
  const [record, setRecord] = useState<
    ItemDonationRecord | ScrapDonationRecord | null
  >(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const scrapRecord =
    variant === "scrap" ? (record as ScrapDonationRecord | null) : null;
  const inKindRecord =
    variant === "in-kind" ? (record as ItemDonationRecord | null) : null;

  useEffect(() => {
    if (!open) {
      setRecord(null);
      setError(null);
      return;
    }
    if (!requestId) {
      return;
    }

    let ignore = false;
    setLoading(true);
    setError(null);
    setRecord(preview ?? null);

    const fetchDetails =
      variant === "scrap"
        ? adminScrapDonationService.get
        : adminItemDonationService.get;

    void fetchDetails(requestId)
      .then(data => {
        if (ignore) {
          return;
        }
        if (data) {
          setRecord(data);
        } else {
          setError("Request details could not be loaded.");
        }
      })
      .catch(err => {
        if (!ignore) {
          setError(getApiErrorMessage(err, "Failed to load request details."));
        }
      })
      .finally(() => {
        if (!ignore) {
          setLoading(false);
        }
      });

    return () => {
      ignore = true;
    };
  }, [open, requestId, preview, variant]);

  const title =
    variant === "scrap" ? "Scrap Donation Request" : "In-Kind Donation Request";

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="flex max-h-[92vh] max-w-4xl flex-col gap-0 overflow-hidden p-0 sm:max-w-4xl">
        <DialogHeader className="shrink-0 border-b border-border px-6 py-4 text-left">
          <div className="flex flex-wrap items-start justify-between gap-3 pr-8">
            <div>
              <DialogTitle className="font-serif text-xl font-bold">
                {record ? `${title} #${record.id}` : title}
              </DialogTitle>
              <p className="mt-1 text-sm text-muted-foreground">
                {record
                  ? `${record.full_name} · submitted ${formatDate(record.created_at) || "—"}`
                  : "Read-only view of the donation request"}
              </p>
            </div>
            {record && statusBadge(record.status)}
          </div>
        </DialogHeader>

        <div className="min-h-0 flex-1 overflow-y-auto px-6 py-5">
          {loading && !record ? (
            <div className="flex flex-col items-center justify-center py-16 text-muted-foreground">
              <Loader2 className="mb-3 h-8 w-8 animate-spin" />
              Loading request details…
            </div>
          ) : error && !record ? (
            <div className="border-destructive/30 bg-destructive/5 rounded-xl border px-4 py-6 text-center text-sm text-destructive">
              {error}
            </div>
          ) : record ? (
            <div className="space-y-6">
              {/* ── Donor Information ── */}
              <SectionPanel section={sections[0]}>
                <ViewField label="Full Name" value={record.full_name} />
                <ViewField label="Mobile" value={record.mobile_number} />
                <ViewField label="Email" value={record.email} full />
              </SectionPanel>

              {/* ── Item Details ── */}
              <SectionPanel section={sections[1]}>
                <ViewField
                  label="Item Category"
                  value={record.item_category_name ?? record.item_category}
                />
                <ViewField label="Item Name" value={record.item_name} />
                <ViewField
                  label="Quantity"
                  value={
                    variant === "in-kind" && inKindRecord
                      ? formatDonationQuantityLabel(
                          record.quantity,
                          inKindRecord.unit,
                          inKindRecord.unit_qty
                        )
                      : record.quantity
                  }
                />
                {variant === "in-kind" && inKindRecord && (
                  <ViewField
                    label="Preferred Donation Date"
                    value={
                      inKindRecord.preferred_donation_date
                        ? formatDate(inKindRecord.preferred_donation_date)
                        : "—"
                    }
                  />
                )}
                {variant === "scrap" && scrapRecord && (
                  <ViewField
                    label="Item Condition"
                    value={scrapRecord.item_condition ?? "scrap"}
                  />
                )}
              </SectionPanel>

              {/* ── Pickup Details ── */}
              {variant === "scrap" && scrapRecord && (
                <SectionPanel section={sections[2]}>
                  <ViewField
                    label="Pickup Address"
                    value={scrapRecord.pickup_address}
                    full
                  />
                  <ViewField label="Area" value={scrapRecord.area} />
                  <ViewField label="Landmark" value={scrapRecord.landmark} />
                  <ViewField label="Pincode" value={scrapRecord.pincode} />
                  <ViewField
                    label="Pickup Date"
                    value={
                      scrapRecord.pickup_date
                        ? formatDate(scrapRecord.pickup_date)
                        : "—"
                    }
                  />
                  <ViewField
                    label="Pickup Note"
                    value={scrapRecord.pickup_note}
                    full
                  />
                </SectionPanel>
              )}
              {variant === "in-kind" && inKindRecord && (
                <SectionPanel section={sections[2]}>
                  <ViewField
                    label="Pickup Address"
                    value={inKindRecord.pickup_address}
                    full
                  />
                  <ViewField label="Area" value={inKindRecord.area} />
                  <ViewField label="Landmark" value={inKindRecord.landmark} />
                  <ViewField label="Pincode" value={inKindRecord.pincode} />
                  <ViewField
                    label="Pickup Date"
                    value={
                      inKindRecord.pickup_date
                        ? formatDate(inKindRecord.pickup_date)
                        : "—"
                    }
                  />
                  <ViewField
                    label="Pickup Note"
                    value={inKindRecord.pickup_note}
                    full
                  />
                </SectionPanel>
              )}

              {/* ── Notes & Review ── */}
              <SectionPanel section={sections[sections.length - 1]}>
                <ViewField label="Message" value={record.message} full />
                <ViewField label="Admin Remarks" value={record.remarks} full />
                <ViewField
                  label="Status"
                  value={formatDonationRequestStatusLabel(record.status)}
                />
                <ViewField
                  label="Submitted On"
                  value={
                    record.created_at ? formatDate(record.created_at) : "—"
                  }
                />
                <ViewField
                  label="Last Updated"
                  value={
                    record.updated_at ? formatDate(record.updated_at) : "—"
                  }
                />
                {variant === "scrap" && scrapRecord?.photo && (
                  <div className="md:col-span-2">
                    <Label className="text-foreground/80 mb-1.5 block text-xs font-medium">
                      Photo
                    </Label>
                    <div className="overflow-hidden rounded-xl border border-border bg-background p-3">
                      <img
                        src={scrapRecord.photo}
                        alt={`Scrap item submitted by ${record.full_name}`}
                        className="max-h-64 w-full rounded-lg object-contain"
                      />
                    </div>
                  </div>
                )}
              </SectionPanel>
            </div>
          ) : null}
        </div>
      </DialogContent>
    </Dialog>
  );
}
